import {useRef, useEffect, useCallback, useState} from "react";
import {checkTemplate, Lang, Template} from "@pdfme/common";
import { Designer } from "@pdfme/ui";
import {getFontsData, getInputFromTemplate, getPlugins} from "./helper";
import {unstable_usePrompt, useLocation} from "react-router-dom";
import {Backdrop, Box, Button, CircularProgress, Container, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import { generate } from "@pdfme/generator";
import {tokens} from "../../../theme.ts";
import {UnsavedChangesProvider} from "../../../context/UnsavedChangesContext.tsx";
import {api} from "../../../api/api.ts";
import {useAuth} from "../../../context/auth/useAuth.ts";
import MemoryUsageProgress from "../../../components/MemoryUsageProgress.tsx";

const headerHeight = 120;

const DesignerView = () => {
    const {t, i18n} = useTranslation()
    const {user} = useAuth();
    const { state } = useLocation();
    const theme = useTheme();
    const colors = tokens(theme.palette);
    const designerRef = useRef(null);
    const designer = useRef<Designer | null>(null);
    const [loading, setLoading] = useState(true);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [currentTemplateSize, setCurrentTemplateSize] = useState<Template | null>(null);
    if (!state?.template || !state?.measurementPoints || !state?.id) {
        return null;
    }

    unstable_usePrompt({
        message: t('designer.leaveWarning') || "You have unsaved changes. Leave anyway?",
        when: ({ currentLocation, nextLocation }) =>
            hasUnsavedChanges &&
            currentLocation.pathname !== nextLocation.pathname,
    });

    const buildDesigner = useCallback(async () => {
        if (!designerRef.current) return;
        console.log("buildDesigner");
        try {
            const measurementPoints = state.measurementPoints || [];
            checkTemplate(state.template);

            // Warten auf die Schriftart-Daten
            const font = getFontsData();

            const template = state.template as Template;

            // Designer-Instanz erstellen
            designer.current = new Designer({
                domContainer: designerRef.current,
                // @ts-ignore
                template,
                options: {
                    measurementPoints,
                    lang: i18n.language as Lang,
                    font,
                    theme: {
                        token: { colorPrimary: "#54B3E4" },
                    },
                    maxZoom: 250,
                },
                plugins: getPlugins(),
            });
            designer.current.onSaveTemplate(onSaveTemplate);
            // Event-Handler für das Speichern der Vorlage hinzufügen
            designer.current.onSaveTemplate(onSaveTemplate);
            setLoading(false);
        } catch (error) {
            console.error("Fehler beim Erstellen des Designers:", error);
        }
    }, [state.template, state.measurementPoints]);

    useEffect(() => {
        if (designerRef.current) {
            setLoading(true);
            buildDesigner();
            setCurrentTemplateSize(designer.current?.getTemplate() || null);
            designer.current?.onChangeTemplate(() => {
                setHasUnsavedChanges(true);
                setCurrentTemplateSize(designer.current?.getTemplate() || null);
            });
        }

        return () => {
            setHasUnsavedChanges(false);
            designer.current?.destroy();
        };
    }, [designerRef, buildDesigner]);

    useEffect(() => {
        if(designer.current){
            designer.current.updateOptions({lang: i18n.language as Lang});
        }
    }, [i18n.language]);



    const onGeneratePDF = async () => {
        if (!designer.current) return;
        try{
            setLoading(true);
            const template: Template = designer.current.getTemplate();
            const measurementPoints = state.measurementPoints || [];
            const inputs = getInputFromTemplate(template);
            const font = getFontsData();
            try {
                const pdf = await generate({
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    template,
                    inputs,
                    options: {
                        font,
                        measurementPoints,
                        lang: (template.lang as 'en' | 'de' | undefined) || 'en',
                        title: 'PDF',
                    },
                    plugins: getPlugins(),
                });

                const blob = new Blob([pdf.buffer as ArrayBuffer], {type: 'application/pdf'});
                window.open(URL.createObjectURL(blob));
            } catch (e) {
                alert(e + '\n\nCheck the console for full stack trace');
                throw e;
            }
            setLoading(false);
        }catch (error) {
            setLoading(false);
            alert(t('designer.generate.error'));
        }

    };

    const onSaveTemplate = async () => {
        if (!designer.current) return;
        setLoading(true);
        if(user) {
            const template: Template = designer.current.getTemplate();
            const jsonString = JSON.stringify(template);
            const sizeInBytes = new TextEncoder().encode(jsonString).length;
            const sizeInMB = sizeInBytes / (1024 * 1024)
            if(sizeInMB > 10){
                alert(t('designer.save.error', { size: sizeInMB.toFixed(2) }));
                setLoading(false);
                return;
            }else{
                api.putTemplateById(user?.subID, state.id, state.name, template, state.measurementPoints)
                    .then(() => {
                        setHasUnsavedChanges(false);
                        setLoading(false);
                    })
                    .catch(() => {
                        setHasUnsavedChanges(false);
                        setLoading(false);
                        console.log("Error saving template");
                    })
            }
        }
    }

    return (
        <UnsavedChangesProvider>
            <Container maxWidth={false} sx={{ p: 2, backgroundColor: "#f5f5f5", height: "100%", overflow: 'hidden' }}>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center",  color: 'white'}}>
                    <Button onClick={async () => await onSaveTemplate()} sx={{flex: 0.15, color: 'white', backgroundColor: colors.primary[500] }}>{t('designer.save')}</Button>
                    <MemoryUsageProgress jsonData={currentTemplateSize} />
                    <Button onClick={async () => await onGeneratePDF()} sx={{flex: 0.15, color: 'white', backgroundColor: colors.primary[500] }}>{t('designer.generate')}</Button>
                </Box>
                <Box ref={designerRef} sx={{ display: 'flex', alignItems: 'center', height: `calc(100vh - ${headerHeight}px)` }}></Box>
                <Backdrop
                    sx={{
                        color: '#fff',
                        zIndex: (theme) => theme.zIndex.modal + 1
                    }}
                    open={loading}
                >
                    <CircularProgress color="inherit"/>
                </Backdrop>
            </Container>
        </UnsavedChangesProvider>
    );
};

export default DesignerView;
